import React, { createContext, useState, useEffect } from 'react';
import { useBusinessAccounts, BusinessAccount } from '@/hooks/useBusinessAccounts';

export interface BusinessAccountContextType {
  selectedAccountId: string | null;
  selectedAccount: BusinessAccount | null;
  setSelectedAccountId: (accountId: string) => void;
  clearSelectedAccount: () => void;
  setWorkspaceAccountOverride: (accountId: string | null) => void;
  accounts: BusinessAccount[] | null;
  isLoading: boolean;
  /**
   * A request for the accounts list is in flight. `isLoading` is false during a
   * refetch (the cache already holds data), so on its own it reads as "settled"
   * while an answer that can still change is on the wire.
   *
   * Optional, and absent means nothing is in flight.
   */
  isAccountsFetching?: boolean;
  /**
   * False until `WorkspaceProvider` has bridged its workspace's business
   * account in. Before that the selection below is provisional — it comes from
   * localStorage or `accounts[0]` — so a missing account means "not known yet",
   * not "nothing connected". Callers that paint a connect/empty state must wait
   * for this, or it flashes for a frame on every reload.
   *
   * Optional, and absent means settled: a context built without it (a test
   * harness, a mock) must never leave a placeholder open forever.
   */
  workspaceBridgeSettled?: boolean;
}

const isValidStoredId = (v: string | null) => typeof v === 'string' && v.trim().length > 0;

const globalKey = '__BUSINESS_ACCOUNT_CONTEXT__';
const globalObject = typeof globalThis !== 'undefined' ? (globalThis as any) : {};

export const BusinessAccountContext: React.Context<BusinessAccountContextType | undefined> =
  globalObject[globalKey] || createContext<BusinessAccountContextType | undefined>(undefined);

if (globalObject && !globalObject[globalKey]) {
  globalObject[globalKey] = BusinessAccountContext;
}

export const BusinessAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: accounts, isLoading, isPending, isFetching } = useBusinessAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('selectedBusinessAccountId');
      if (isValidStoredId(stored)) return stored;
    }
    return null;
  });

  useEffect(() => {
    if (selectedAccountId && typeof window !== 'undefined') {
      localStorage.setItem('selectedBusinessAccountId', selectedAccountId);
    }
  }, [selectedAccountId]);

  const [workspaceOverride, setWorkspaceOverride] = useState<{ active: boolean; accountId: string | null }>(
    { active: false, accountId: null },
  );
  const setWorkspaceAccountOverride = React.useCallback((accountId: string | null) => {
    setWorkspaceOverride({ active: true, accountId });
  }, []);

  useEffect(() => {
    if (workspaceOverride.active) return;
    if (accounts && accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId, setSelectedAccountId, workspaceOverride.active]);

  useEffect(() => {
  }, [accounts, selectedAccountId]);

  // A stored id that the settled list does not contain is stale — the account
  // was removed, deactivated, or belongs to another org. Falling back keeps the
  // app on a real account instead of resolving to nothing and telling the user
  // they have no WhatsApp account connected. The workspace override is
  // authoritative and is never second-guessed here.
  const storedIdIsStale =
    Boolean(selectedAccountId) &&
    Array.isArray(accounts) &&
    accounts.length > 0 &&
    !accounts.some((account) => account.id === selectedAccountId);

  const resolvedAccountId = workspaceOverride.active
    ? workspaceOverride.accountId
    : (storedIdIsStale
        ? (accounts?.[0]?.id ?? null)
        : (selectedAccountId ?? accounts?.[0]?.id ?? null));

  const selectedAccount = accounts?.find(account => account.id === resolvedAccountId) || null;

  const clearSelectedAccount = () => {
    setSelectedAccountId(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('selectedBusinessAccountId');
    }
  };

  const value: BusinessAccountContextType = {
    selectedAccountId: resolvedAccountId,
    selectedAccount,
    setSelectedAccountId,
    clearSelectedAccount,
    setWorkspaceAccountOverride,
    accounts: accounts || null,
    isLoading: Boolean(isPending) || isLoading,
    isAccountsFetching: Boolean(isFetching),
    workspaceBridgeSettled: workspaceOverride.active,
  };

  return (
    <BusinessAccountContext.Provider value={value}>
      {children}
    </BusinessAccountContext.Provider>
  );
};

export default BusinessAccountProvider;
